import { useMemo, useState } from "react";
import { approvePendingVisit, rejectPendingVisit, runOverstayDetection } from "./api";
import { DashboardCustomizer } from "./DashboardCustomizer";
import { DashboardShell } from "./DashboardShell";
import { SiteSwitcher } from "./SiteSwitcher";
import { useSiteContext } from "./use-site-context";
import { formatDateTime } from "./format";
import type { VisitRow } from "./types";
import { useDashboardData } from "./use-dashboard-data";
import { useWidgetPrefs } from "./useWidgetPrefs";
import { EmptyRow, PendingApprovalList, RecentVisitorList } from "./ui";
import { ADMIN_HR_DEFAULT_WIDGETS, ADMIN_HR_WIDGETS } from "./widgets";
import { VisitorsPanel } from "../admin/VisitorsPanel";
import { AuditPanel } from "../admin/AuditPanel";
import { UsersPanel } from "../admin/UsersPanel";
import { HostsPanel } from "../admin/HostsPanel";
import { SitesPanel } from "../admin/SitesPanel";
import { VisitorFieldsPanel } from "../admin/VisitorFieldsPanel";
import { BackupPanel } from "../admin/BackupPanel";
import { GateQrPanel } from "../admin/GateQrPanel";
import { RecurringPassesPanel } from "../admin/RecurringPassesPanel";
import { EmailTemplatesPanel } from "../admin/EmailTemplatesPanel";
import { GatePassTemplatesPanel } from "../admin/GatePassTemplatesPanel";
import { SlaSettingsPanel } from "../admin/SlaSettingsPanel";
import { EmailConfigPanel } from "../admin/EmailConfigPanel";
import { BrandingSettingsPanel } from "../admin/BrandingSettingsPanel";
import { BillingPanel } from "../admin/BillingPanel";
import { ChartsPanel } from "./ChartsPanel";
import { InviteVisitorForm } from "./InviteVisitorForm";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";
import { parseUtcTimestamp } from "../../lib/parse-utc";

type AdminScreen =
  | "operations"
  | "invite"
  | "approvals"
  | "alerts"
  | "tools"
  | "insights"
  | "visitors"
  | "audit"
  | "users"
  | "hosts"
  | "sites"
  | "visitor-fields"
  | "gate-qr"
  | "passes"
  | "email-templates"
  | "gate-pass-templates"
  | "sla-settings"
  | "email-config"
  | "branding"
  | "billing"
  | "backup";

const SCREEN_OPTIONS: { key: AdminScreen; label: string; summary: string }[] = [
  {
    key: "invite",
    label: "Invite Visitor",
    summary: "Send a visitor an invite link, or issue a direct-entry gate pass.",
  },
  {
    key: "operations",
    label: "Operations",
    summary: "Track inside visitors and active floor traffic.",
  },
  {
    key: "approvals",
    label: "Approvals",
    summary: "Monitor queue aging and host response pressure.",
  },
  {
    key: "alerts",
    label: "Alerts",
    summary: "Review overstay risk and escalation priority.",
  },
  {
    key: "tools",
    label: "Tools",
    summary: "Run admin controls and operational maintenance.",
  },
  {
    key: "insights",
    label: "Insights",
    summary: "Visit trends, status mix, and busiest hosts at a glance.",
  },
  {
    key: "visitors",
    label: "Visitors",
    summary: "Browse the visitor directory and manage blacklist status.",
  },
  {
    key: "audit",
    label: "Audit",
    summary: "Inspect the audit trail for visits, gate actions, and approvals.",
  },
  {
    key: "users",
    label: "Users",
    summary: "Create and manage backend user accounts and roles.",
  },
  {
    key: "hosts",
    label: "Hosts",
    summary: "Create and manage host directory records.",
  },
  {
    key: "sites",
    label: "Sites",
    summary: "Manage locations/branches and scope users to a single site.",
  },
  {
    key: "visitor-fields",
    label: "Visitor Fields",
    summary: "Choose which visitor details are mandatory at check-in and invite.",
  },
  {
    key: "gate-qr",
    label: "Gate QR",
    summary: "Print a QR for visitors to scan and self-register at the gate.",
  },
  {
    key: "passes",
    label: "Long-term Passes",
    summary: "Issue reusable monthly/yearly/permanent badges and approve host requests.",
  },
  {
    key: "email-templates",
    label: "Email Templates",
    summary: "Customize the subject and message text of automated emails.",
  },
  {
    key: "gate-pass-templates",
    label: "Gate Pass Templates",
    summary: "Design and switch between visitor gate pass looks.",
  },
  {
    key: "sla-settings",
    label: "SLA Settings",
    summary: "Configure approval queue aging thresholds.",
  },
  {
    key: "email-config",
    label: "Email / SMTP",
    summary: "Send visitor emails from your own SMTP server instead of the shared relay.",
  },
  {
    key: "branding",
    label: "Branding",
    summary: "Upload a company logo and favicon, or revert to default branding.",
  },
  {
    key: "billing",
    label: "Billing",
    summary: "View your plan, usage, and subscribe or upgrade.",
  },
  {
    key: "backup",
    label: "Backup",
    summary: "Download a data backup or restore your company from one.",
  },
];

// The everyday screens stay in one always-visible row; everything else is
// tucked behind a "More" toggle, grouped by purpose, so the daily workflow
// isn't buried under 20 flat buttons.
const PRIMARY_SCREENS: AdminScreen[] = [
  "operations",
  "invite",
  "approvals",
  "alerts",
  "visitors",
  "insights",
];

const SCREEN_GROUPS: { label: string; keys: AdminScreen[] }[] = [
  { label: "People & Places", keys: ["users", "hosts", "sites"] },
  { label: "Passes & Gate", keys: ["passes", "gate-qr"] },
  {
    label: "Customization",
    keys: [
      "visitor-fields",
      "branding",
      "email-templates",
      "gate-pass-templates",
      "email-config",
      "sla-settings",
    ],
  },
  { label: "System", keys: ["audit", "tools", "billing", "backup"] },
];

// The header search only filters the visit lists on these screens; hide it on
// the others (Invite, Insights, settings panels, etc.) where it does nothing.
const SEARCH_SCREENS: AdminScreen[] = ["operations", "approvals", "alerts"];

const SCREEN_META_BY_KEY: Record<AdminScreen, { label: string; summary: string }> =
  Object.fromEntries(SCREEN_OPTIONS.map((o) => [o.key, { label: o.label, summary: o.summary }])) as Record<
    AdminScreen,
    { label: string; summary: string }
  >;

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function matchesSearch(visit: VisitRow, query: string): boolean {
  if (!query) return true;
  const q = normalize(query);
  const pool = [
    String(visit.visit_id),
    visit.visitor_name,
    visit.host_name,
    visit.company,
    visit.purpose,
    visit.badge_code,
    visit.status,
  ]
    .filter(Boolean)
    .map((item) => normalize(String(item)));

  return pool.some((item) => item.includes(q));
}

function getElapsedMinutes(value?: string): number {
  if (!value) return 0;
  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) return 0;
  const diff = Date.now() - date.getTime();
  return Math.max(0, Math.floor(diff / 60000));
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
}

function getWaitClass(minutes: number, warningMinutes = 60, criticalMinutes = 120): string {
  if (minutes >= criticalMinutes) return "wait-chip wait-critical";
  if (minutes >= warningMinutes) return "wait-chip wait-warning";
  return "wait-chip wait-normal";
}

function getStatusClass(status?: string): string {
  const normalized = (status || "").toUpperCase();
  if (normalized === "INSIDE") return "status-pill status-inside";
  if (normalized === "OVERSTAY") return "status-pill status-overstay";
  if (normalized === "PENDING") return "status-pill status-pending";
  if (normalized === "APPROVED") return "status-pill status-approved";
  if (normalized === "CHECKED_OUT") return "status-pill status-checked-out";
  if (normalized === "REJECTED") return "status-pill status-rejected";
  return "status-pill";
}

function OperationsTable({ rows }: { rows: VisitRow[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Visitor</th>
            <th>Company</th>
            <th>Host</th>
            <th>Check-in</th>
            <th>Expected Exit</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow label="No active inside visitors" colSpan={6} />
          ) : (
            rows.map((visit) => (
              <tr key={`admin-inside-${visit.visit_id}`}>
                <td>{visit.visitor_name}</td>
                <td>{visit.company || "-"}</td>
                <td>{visit.host_name || "-"}</td>
                <td>{formatDateTime(visit.checkin_time)}</td>
                <td>{formatDateTime(visit.expected_exit)}</td>
                <td>
                  <span className={getStatusClass(visit.status || "INSIDE")}>
                    {visit.status || "INSIDE"}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

type ApprovalAction = "approve" | "reject";

function getApprovalActionKey(action: ApprovalAction, visitId: number) {
  return `${action}-${visitId}`;
}

function ApprovalQueueTable({
  rows,
  onAction,
  pendingKey,
  warningMinutes,
  criticalMinutes,
}: {
  rows: VisitRow[];
  onAction: (action: ApprovalAction, visitId: number) => Promise<void>;
  pendingKey: string | null;
  warningMinutes: number;
  criticalMinutes: number;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Visit</th>
            <th>Visitor</th>
            <th>Host</th>
            <th>Purpose</th>
            <th>Requested</th>
            <th>Wait</th>
            <th>Badge</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow label="No approvals waiting" colSpan={8} />
          ) : (
            rows.map((visit) => {
              const waitMinutes = getElapsedMinutes(visit.created_at);
              const approveKey = getApprovalActionKey("approve", visit.visit_id);
              const rejectKey = getApprovalActionKey("reject", visit.visit_id);
              const isBusy = pendingKey === approveKey || pendingKey === rejectKey;
              return (
                <tr key={`admin-pending-${visit.visit_id}`}>
                  <td>#{visit.visit_id}</td>
                  <td>{visit.visitor_name}</td>
                  <td>{visit.host_name || "-"}</td>
                  <td>{visit.purpose || "-"}</td>
                  <td>{formatDateTime(visit.created_at)}</td>
                  <td>
                    <span className={getWaitClass(waitMinutes, warningMinutes, criticalMinutes)}>
                      {formatMinutes(waitMinutes)}
                    </span>
                  </td>
                  <td>{visit.badge_code || "-"}</td>
                  <td className="action-cell">
                    <button
                      className="small-btn success-btn"
                      type="button"
                      onClick={() => void onAction("approve", visit.visit_id)}
                      disabled={isBusy}
                    >
                      {pendingKey === approveKey ? "Approving..." : "Approve"}
                    </button>
                    <button
                      className="small-btn danger-btn"
                      type="button"
                      onClick={() => void onAction("reject", visit.visit_id)}
                      disabled={isBusy}
                    >
                      {pendingKey === rejectKey ? "Rejecting..." : "Reject"}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function AlertEscalationTable({ rows }: { rows: VisitRow[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Visit</th>
            <th>Visitor</th>
            <th>Host</th>
            <th>Check-in</th>
            <th>Expected Exit</th>
            <th>Overdue</th>
            <th>Severity</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow label="No overstay alerts right now" colSpan={7} />
          ) : (
            rows.map((visit) => {
              const overdueMinutes = getElapsedMinutes(visit.expected_exit);
              const severity =
                overdueMinutes >= 180 ? "High" : overdueMinutes >= 90 ? "Medium" : "Low";
              return (
                <tr key={`admin-overstay-${visit.visit_id}`}>
                  <td>#{visit.visit_id}</td>
                  <td>{visit.visitor_name}</td>
                  <td>{visit.host_name || "-"}</td>
                  <td>{formatDateTime(visit.checkin_time)}</td>
                  <td>{formatDateTime(visit.expected_exit)}</td>
                  <td>
                    <span className={getWaitClass(overdueMinutes)}>
                      {formatMinutes(overdueMinutes)}
                    </span>
                  </td>
                  <td>
                    <span className={severity === "High" ? "status-pill status-overstay" : "status-pill"}>
                      {severity}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AdminHrDashboardPage() {
  const siteContext = useSiteContext();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const { data, loading, refreshing, error, lastUpdated, statsCards, refresh } =
    useDashboardData(selectedSiteId);
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [activeScreen, setActiveScreen] = useState<AdminScreen>("operations");
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [toolRunning, setToolRunning] = useState(false);
  const [toolMessage, setToolMessage] = useState<string | null>(null);
  const [toolError, setToolError] = useState<string | null>(null);
  const [approvalPendingKey, setApprovalPendingKey] = useState<string | null>(null);
  const { widgetIds, saving, save, resetToDefault } = useWidgetPrefs(ADMIN_HR_DEFAULT_WIDGETS);
  const [showCustomizer, setShowCustomizer] = useState(false);

  const recentVisitors = useMemo(() => data?.recentVisitors ?? [], [data]);
  const pendingApprovals = useMemo(() => data?.pendingApprovals ?? [], [data]);
  const alerts = data?.alerts;
  const insideVisitors = useMemo(() => data?.summary.inside ?? [], [data]);
  const overstayVisitors = useMemo(() => data?.summary.overstay ?? [], [data]);

  const filteredInside = useMemo(
    () => insideVisitors.filter((visit) => matchesSearch(visit, searchQuery)),
    [insideVisitors, searchQuery],
  );
  const filteredPending = useMemo(
    () => pendingApprovals.filter((visit) => matchesSearch(visit, searchQuery)),
    [pendingApprovals, searchQuery],
  );
  const filteredOverstay = useMemo(
    () => overstayVisitors.filter((visit) => matchesSearch(visit, searchQuery)),
    [overstayVisitors, searchQuery],
  );

  const slaWarningMinutes = data?.companySettings.approval_sla_warning_minutes ?? 60;
  const slaCriticalMinutes = data?.companySettings.approval_sla_critical_minutes ?? 120;

  const approvalsOverWarning = filteredPending.filter(
    (visit) => getElapsedMinutes(visit.created_at) >= slaWarningMinutes,
  ).length;
  const approvalsOverCritical = filteredPending.filter(
    (visit) => getElapsedMinutes(visit.created_at) >= slaCriticalMinutes,
  ).length;

  const activeScreenMeta = SCREEN_OPTIONS.find((option) => option.key === activeScreen)!;

  async function runOverstayScan() {
    setToolRunning(true);
    setToolMessage(null);
    setToolError(null);
    try {
      const result = await runOverstayDetection();
      setToolMessage(`Overstay scan completed. ${result.overstays_marked} visit(s) updated.`);
      await refresh();
    } catch (requestError) {
      setToolError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to run overstay detection",
      );
    } finally {
      setToolRunning(false);
    }
  }

  async function handleApprovalAction(action: ApprovalAction, visitId: number) {
    if (action === "reject") {
      const ok = await confirm({
        title: "Reject this visit?",
        message: `Visit #${visitId} will be marked REJECTED. This can't be undone from here.`,
        confirmLabel: "Reject",
        danger: true,
      });
      if (!ok) return;
    }

    const key = getApprovalActionKey(action, visitId);
    setApprovalPendingKey(key);
    try {
      const result =
        action === "approve" ? await approvePendingVisit(visitId) : await rejectPendingVisit(visitId);
      showSuccess(`Visit ${result.visit_id} updated to ${result.status}`);
      await refresh();
    } catch (requestError) {
      showError(
        requestError instanceof Error ? requestError.message : "Unable to update approval status",
      );
    } finally {
      setApprovalPendingKey(null);
    }
  }

  return (
    <DashboardShell
      roleName="Admin-HR"
      title="Management Console"
      subtitle="Operate, audit, and escalate visitor workflows from centralized management screens."
      loading={loading}
      refreshing={refreshing}
      error={error}
      lastUpdated={lastUpdated}
      onRefresh={refresh}
      siteSwitcher={
        <SiteSwitcher context={siteContext} value={selectedSiteId} onChange={setSelectedSiteId} />
      }
      showCustomizer={showCustomizer}
      onToggleCustomize={() => setShowCustomizer((prev) => !prev)}
      customizerPanel={
        <DashboardCustomizer
          catalog={ADMIN_HR_WIDGETS}
          value={widgetIds}
          saving={saving}
          onSave={async (ids) => {
            await save(ids);
            setShowCustomizer(false);
          }}
          onResetToDefault={async () => {
            await resetToDefault();
            setShowCustomizer(false);
          }}
          onClose={() => setShowCustomizer(false)}
        />
      }
    >
      <section className="stats-grid fade-in delay-1">
        {statsCards.map((card) => (
          <article className="stat-card" key={`admin-card-${card.label}`}>
            <p className="stat-label">{card.label}</p>
            <p className="stat-value">{card.value}</p>
          </article>
        ))}
      </section>

      <section className="panel fade-in delay-2">
        <header className="panel-header">
          <h2>Management Screens</h2>
          <span>{activeScreenMeta.label}</span>
        </header>
        <p className="management-note">{activeScreenMeta.summary}</p>
        <div className="screen-switch">
          {PRIMARY_SCREENS.map((key) => (
            <button
              key={key}
              type="button"
              className={key === activeScreen ? "screen-btn active" : "screen-btn"}
              onClick={() => setActiveScreen(key)}
            >
              {SCREEN_META_BY_KEY[key].label}
            </button>
          ))}
          <button
            type="button"
            className={
              moreOpen || !PRIMARY_SCREENS.includes(activeScreen)
                ? "screen-btn more-toggle active"
                : "screen-btn more-toggle"
            }
            aria-expanded={moreOpen || !PRIMARY_SCREENS.includes(activeScreen)}
            onClick={() => setMoreOpen((prev) => !prev)}
          >
            More {moreOpen || !PRIMARY_SCREENS.includes(activeScreen) ? "▲" : "▾"}
          </button>
        </div>

        {moreOpen || !PRIMARY_SCREENS.includes(activeScreen) ? (
          <div className="screen-groups">
            {SCREEN_GROUPS.map((group) => (
              <div className="screen-group" key={group.label}>
                <p className="screen-group-label">{group.label}</p>
                <div className="screen-switch">
                  {group.keys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={key === activeScreen ? "screen-btn active" : "screen-btn"}
                      onClick={() => setActiveScreen(key)}
                    >
                      {SCREEN_META_BY_KEY[key].label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {SEARCH_SCREENS.includes(activeScreen) ? (
          <div className="management-toolbar">
            <label className="toolbar-label" htmlFor="admin-screen-search">
              Search records
            </label>
            <input
              id="admin-screen-search"
              className="search-input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by visitor, host, company, badge, status, or visit ID"
            />
          </div>
        ) : null}
      </section>

      {activeScreen === "operations" ? (
        <section className="panel-grid fade-in delay-3">
          {widgetIds.map((widgetId) => {
            if (widgetId === "inside-visitors") {
              return (
                <article className="panel" key="inside-visitors">
                  <header className="panel-header">
                    <h2>Inside Visitors</h2>
                    <span>{filteredInside.length} rows</span>
                  </header>
                  <OperationsTable rows={filteredInside} />
                </article>
              );
            }
            if (widgetId === "recent-visitors") {
              return (
                <article className="panel" key="recent-visitors">
                  <header className="panel-header">
                    <h2>Recent Visitors</h2>
                    <span>{recentVisitors.length} rows</span>
                  </header>
                  <ul className="list-stack">
                    <RecentVisitorList rows={recentVisitors} />
                  </ul>
                </article>
              );
            }
            if (widgetId === "queue-aging") {
              return (
                <article className="panel" key="queue-aging">
                  <header className="panel-header">
                    <h2>Queue Aging</h2>
                    <span>Service Level</span>
                  </header>
                  <div className="aging-grid">
                    <div className="aging-card">
                      <p className="stat-label">Waiting {formatMinutes(slaWarningMinutes)}+</p>
                      <p className="stat-value">{approvalsOverWarning}</p>
                    </div>
                    <div className="aging-card">
                      <p className="stat-label">Waiting {formatMinutes(slaCriticalMinutes)}+</p>
                      <p className="stat-value">{approvalsOverCritical}</p>
                    </div>
                    <div className="aging-card">
                      <p className="stat-label">Total Pending</p>
                      <p className="stat-value">{filteredPending.length}</p>
                    </div>
                  </div>
                  <ul className="list-stack">
                    <PendingApprovalList rows={filteredPending.slice(0, 8)} />
                  </ul>
                </article>
              );
            }
            if (widgetId === "alert-snapshot") {
              return (
                <article className="panel alert-panel" key="alert-snapshot">
                  <header className="panel-header">
                    <h2>Alert Snapshot</h2>
                    <span>Live</span>
                  </header>
                  <div className="alert-metrics">
                    <div>
                      <p className="stat-label">Overstay Alerts</p>
                      <p className="stat-value">{alerts?.overstay_count ?? 0}</p>
                    </div>
                    <div>
                      <p className="stat-label">Pending Alerts</p>
                      <p className="stat-value">{alerts?.pending_approval_count ?? 0}</p>
                    </div>
                  </div>
                  <p className="alert-note">
                    Prioritize high overdue visits, then approvals queued longer than{" "}
                    {formatMinutes(slaWarningMinutes)}.
                  </p>
                </article>
              );
            }
            if (widgetId === "operational-counters") {
              return (
                <article className="panel" key="operational-counters">
                  <header className="panel-header">
                    <h2>Operational Counters</h2>
                    <span>Current</span>
                  </header>
                  <div className="aging-grid">
                    <div className="aging-card">
                      <p className="stat-label">Inside Visitors</p>
                      <p className="stat-value">{insideVisitors.length}</p>
                    </div>
                    <div className="aging-card">
                      <p className="stat-label">Overstay Visitors</p>
                      <p className="stat-value">{overstayVisitors.length}</p>
                    </div>
                    <div className="aging-card">
                      <p className="stat-label">Pending Approvals</p>
                      <p className="stat-value">{pendingApprovals.length}</p>
                    </div>
                  </div>
                </article>
              );
            }
            return null;
          })}
        </section>
      ) : null}

      {activeScreen === "approvals" ? (
        <section className="panel-grid-full fade-in delay-3">
          <article className="panel">
            <header className="panel-header">
              <h2>Pending Approval Queue</h2>
              <span>{filteredPending.length} rows</span>
            </header>
            <ApprovalQueueTable
              rows={filteredPending}
              onAction={handleApprovalAction}
              pendingKey={approvalPendingKey}
              warningMinutes={slaWarningMinutes}
              criticalMinutes={slaCriticalMinutes}
            />
          </article>

          <article className="panel">
            <header className="panel-header">
              <h2>Queue Aging</h2>
              <span>Service Level</span>
            </header>
            <div className="aging-grid">
              <div className="aging-card">
                <p className="stat-label">Waiting {formatMinutes(slaWarningMinutes)}+</p>
                <p className="stat-value">{approvalsOverWarning}</p>
              </div>
              <div className="aging-card">
                <p className="stat-label">Waiting {formatMinutes(slaCriticalMinutes)}+</p>
                <p className="stat-value">{approvalsOverCritical}</p>
              </div>
              <div className="aging-card">
                <p className="stat-label">Total Pending</p>
                <p className="stat-value">{filteredPending.length}</p>
              </div>
            </div>
            <ul className="list-stack">
              <PendingApprovalList rows={filteredPending.slice(0, 8)} />
            </ul>
          </article>
        </section>
      ) : null}

      {activeScreen === "alerts" ? (
        <section className="panel-grid fade-in delay-3">
          <article className="panel">
            <header className="panel-header">
              <h2>Overstay Escalations</h2>
              <span>{filteredOverstay.length} rows</span>
            </header>
            <AlertEscalationTable rows={filteredOverstay} />
          </article>

          <article className="panel alert-panel">
            <header className="panel-header">
              <h2>Alert Snapshot</h2>
              <span>Live</span>
            </header>
            <div className="alert-metrics">
              <div>
                <p className="stat-label">Overstay Alerts</p>
                <p className="stat-value">{alerts?.overstay_count ?? 0}</p>
              </div>
              <div>
                <p className="stat-label">Pending Alerts</p>
                <p className="stat-value">{alerts?.pending_approval_count ?? 0}</p>
              </div>
            </div>
            <p className="alert-note">
              Prioritize high overdue visits, then approvals queued longer than{" "}
              {formatMinutes(slaWarningMinutes)}.
            </p>
          </article>
        </section>
      ) : null}

      {activeScreen === "tools" ? (
        <section className="panel-grid fade-in delay-3">
          <article className="panel">
            <header className="panel-header">
              <h2>Admin Tools</h2>
              <span>Maintenance</span>
            </header>
            <div className="tool-card">
              <p>
                Trigger a manual overstay scan to immediately mark overdue `INSIDE` visits as
                `OVERSTAY`.
              </p>
              <div className="tool-actions">
                <button
                  type="button"
                  className="refresh-btn"
                  onClick={() => void runOverstayScan()}
                  disabled={toolRunning}
                >
                  {toolRunning ? "Running..." : "Run Overstay Detection"}
                </button>
              </div>
            </div>
            {toolMessage ? <p className="success-banner">{toolMessage}</p> : null}
            {toolError ? <p className="error-banner action-error">{toolError}</p> : null}
          </article>

          <article className="panel">
            <header className="panel-header">
              <h2>Operational Counters</h2>
              <span>Current</span>
            </header>
            <div className="aging-grid">
              <div className="aging-card">
                <p className="stat-label">Inside Visitors</p>
                <p className="stat-value">{insideVisitors.length}</p>
              </div>
              <div className="aging-card">
                <p className="stat-label">Overstay Visitors</p>
                <p className="stat-value">{overstayVisitors.length}</p>
              </div>
              <div className="aging-card">
                <p className="stat-label">Pending Approvals</p>
                <p className="stat-value">{pendingApprovals.length}</p>
              </div>
            </div>
          </article>
        </section>
      ) : null}

      {activeScreen === "invite" ? (
        <section className="fade-in delay-3">
          <InviteVisitorForm />
        </section>
      ) : null}

      {activeScreen === "insights" ? (
        <section className="fade-in delay-3">
          <ChartsPanel />
        </section>
      ) : null}

      {activeScreen === "visitors" ? (
        <section className="panel-grid-full fade-in delay-3">
          <VisitorsPanel />
        </section>
      ) : null}

      {activeScreen === "audit" ? (
        <section className="panel-grid-full fade-in delay-3">
          <AuditPanel />
        </section>
      ) : null}

      {activeScreen === "users" ? (
        <section className="panel-grid-full fade-in delay-3">
          <UsersPanel />
        </section>
      ) : null}

      {activeScreen === "hosts" ? (
        <section className="panel-grid-full fade-in delay-3">
          <HostsPanel />
        </section>
      ) : null}

      {activeScreen === "sites" ? (
        <section className="panel-grid-full fade-in delay-3">
          <SitesPanel />
        </section>
      ) : null}

      {activeScreen === "visitor-fields" ? (
        <section className="panel-grid-full fade-in delay-3">
          <VisitorFieldsPanel />
        </section>
      ) : null}

      {activeScreen === "gate-qr" ? (
        <section className="panel-grid-full fade-in delay-3">
          <GateQrPanel />
        </section>
      ) : null}

      {activeScreen === "email-templates" ? (
        <section className="panel-grid-full fade-in delay-3">
          <EmailTemplatesPanel />
        </section>
      ) : null}

      {activeScreen === "gate-pass-templates" ? (
        <section className="panel-grid-full fade-in delay-3">
          <GatePassTemplatesPanel />
        </section>
      ) : null}

      {activeScreen === "sla-settings" ? (
        <section className="panel-grid-full fade-in delay-3">
          <SlaSettingsPanel />
        </section>
      ) : null}

      {activeScreen === "email-config" ? (
        <section className="panel-grid-full fade-in delay-3">
          <EmailConfigPanel />
        </section>
      ) : null}

      {activeScreen === "branding" ? (
        <section className="panel-grid-full fade-in delay-3">
          <BrandingSettingsPanel />
        </section>
      ) : null}

      {activeScreen === "billing" ? (
        <section className="panel-grid-full fade-in delay-3">
          <BillingPanel />
        </section>
      ) : null}

      {activeScreen === "backup" ? (
        <section className="panel-grid-full fade-in delay-3">
          <BackupPanel />
        </section>
      ) : null}

      {activeScreen === "passes" ? (
        <section className="panel-grid-full fade-in delay-3">
          <RecurringPassesPanel />
        </section>
      ) : null}
    </DashboardShell>
  );
}
